import { rgba } from 'polished';
import styled from 'styled-components';

import { cFFFFFF } from '#/colors';

/**
 * Component that loops through all own properties of data
 * and renders each property as a new component.
 */
export default function SmartJson(props: {
  /**
   * Data to render.
   */
  readonly data: unknown;
  /**
   * Function to be used to render primitive values.
   * @param value Value to render.
   * @returns Rendered value.
   */
  readonly renderValue: (value: unknown) => React.ReactNode;
}) {
  const { data } = props;

  if (data == null) return String(data);

  if (Array.isArray(data)) {
    return <SmartArray value={data} render={props.renderValue} />;
  }

  // Render primitive values
  if (typeof data !== 'object') return props.renderValue(data);

  // Not a pure object, represent as string (e.g. BigNumber)
  if (Object.getPrototypeOf(data) !== Object.prototype) return String(data);

  // Object, keep rendering recursively
  return (
    <ObjectBlock>
      {Object.entries(data as Record<string, unknown>).map(
        ([entryKey, entryValue]) => (
          <div key={entryKey}>
            <Key>{entryKey}: </Key>
            <SmartJson data={entryValue} renderValue={props.renderValue} />
          </div>
        ),
      )}
    </ObjectBlock>
  );
}

function SmartArray(props: {
  readonly value: readonly unknown[];
  readonly render: (value: unknown) => React.ReactNode;
}) {
  return (
    <ArrayBlock>
      {props.value.map((entry, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index}>
          <SmartJson data={entry} renderValue={props.render} />
        </div>
      ))}
    </ArrayBlock>
  );
}

const Key = styled.span`
  color: ${rgba(cFFFFFF, 0.6)};
  font-size: 12px;
`;

const ObjectBlock = styled.pre`
  display: grid;
  gap: 8px;
  background: ${rgba(cFFFFFF, 0.03)};
  border-radius: 7px;
  color: ${rgba(cFFFFFF, 0.8)};
  font-size: 12px;
  line-height: 16px;
  margin: 0;
  padding: 12px 16px;
  overflow-x: auto;
`;

const ArrayBlock = styled.div`
  display: grid;
  gap: 8px;
  padding: 12px 12px;
`;
